<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { getFileInfo } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { downloadBlob } from 'dashboard/helper/downloadHelper';

import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const store = useStore();

const { t } = useI18n();

const fileDetails = computed(() => {
  return getFileInfo(attachment?.dataUrl || '');
});

const downloadFile = async () => {
  try {
    const response = await fetch(attachment.dataUrl);
    const blob = await response.blob();
    downloadBlob(blob, fileDetails.value.name);
  } catch (error) {
    useAlert(t('CONVERSATION.ERROR_DOWNLOADING_FILE'));
  }
};

const displayFileName = computed(() => {
  const { base, type } = fileDetails.value;
  const currentAccount = store.getters['accounts/getCurrentAccount'];
  const showFull = currentAccount?.show_full_attachment_names || false;

  if (showFull) {
    return type ? `${base}.${type}` : base;
  }

  const truncatedName = (str, maxLength, hasExt) =>
    str.length > maxLength
      ? `${str.substring(0, maxLength).trimEnd()}${hasExt ? '..' : '...'}`
      : str;

  return type
    ? `${truncatedName(base, 12, true)}.${type}`
    : truncatedName(base, 14, false);
});

const showFullNames = computed(() => {
  const currentAccount = store.getters['accounts/getCurrentAccount'];
  return currentAccount?.show_full_attachment_names || false;
});

const textColorClass = computed(() => {
  const colorMap = {
    '7z': 'dark:text-[#EDEEF0] text-[#2F265F]',
    csv: 'text-amber-12',
    doc: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    docx: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    json: 'text-n-slate-12',
    odt: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    pdf: 'text-n-slate-12',
    ppt: 'dark:text-[#FFE0C2] text-[#582D1D]',
    pptx: 'dark:text-[#FFE0C2] text-[#582D1D]',
    rar: 'dark:text-[#EDEEF0] text-[#2F265F]',
    rtf: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    tar: 'dark:text-[#EDEEF0] text-[#2F265F]',
    txt: 'text-n-slate-12',
    xls: 'text-n-teal-12',
    xlsx: 'text-n-teal-12',
    zip: 'dark:text-[#EDEEF0] text-[#2F265F]',
  };

  return colorMap[fileDetails.value.type] || 'text-n-slate-12';
});
</script>

<template>
  <div
    class="bg-n-alpha-white gap-2 items-center flex px-2 rounded-lg border border-n-container"
    :class="showFullNames ? 'min-h-9 py-1' : 'h-9 overflow-hidden'"
  >
    <FileIcon class="flex-shrink-0" :file-type="fileDetails.type" />
    <a
      v-tooltip="t('CONVERSATION.OPEN_FILE')"
      :href="attachment.dataUrl"
      target="_blank"
      rel="noreferrer noopener nofollow"
      class="flex-1 min-w-0 text-sm hover:underline cursor-pointer"
      :class="[
        textColorClass,
        showFullNames ? 'break-all' : 'truncate max-w-36',
      ]"
    >
      {{ displayFileName }}
    </a>
    <button
      v-tooltip="t('CONVERSATION.DOWNLOAD')"
      class="flex-shrink-0 size-9 grid place-content-center cursor-pointer text-n-slate-11 hover:text-n-slate-12 transition-colors"
      @click="downloadFile"
    >
      <Icon icon="i-lucide-download" />
    </button>
  </div>
</template>
